<template>
  <div class="custom-upload-review container-fluid p-3">
    <h5 class="mb-1">Estimate Upload Review</h5>
    <p class="text-muted mb-3">
      Estimates uploaded by your firm and fellow associates.
    </p>

    <div v-if="notConfigured" class="alert alert-info">
      <h6 class="mb-1"><i class="fas fa-info-circle me-1"></i>ADAS Map isn't enabled for this location</h6>
      <div>{{ errorMessage }}</div>
    </div>
    <div v-else-if="errorMessage" class="alert alert-warning py-2">{{ errorMessage }}</div>

    <div class="d-flex align-items-end gap-2 mb-2">
      <!-- A super admin's own firm is an admin firm, not a shop, so there is no ADAS Map target to
           resolve from their token. They pick the firm instead; everyone else is scoped to their own. -->
      <div v-if="isSuperAdmin" class="custom-firm-picker">
        <label class="custom-label mb-0">Firm</label>
        <Multiselect
          v-model="selectedFirm"
          class="form-control custom-multiselect"
          placeholder="search firm by name"
          :close-on-select="true"
          :filter-results="false"
          :min-chars="1"
          :resolve-on-load="false"
          :delay="400"
          :searchable="true"
          :options="async (query) => await fetchLocations(query)"
          label="firmName"
          value-prop="firm"
          @change="load"
        />
      </div>
      <button class="btn btn-outline-secondary btn-sm" :disabled="loading" @click="load">
        <i class="fas fa-sync me-1"></i>{{ loading ? 'Loading…' : 'Refresh' }}
      </button>
      <nav v-if="pages > 1" class="ms-auto custom-pager">
        <ZPaginate v-model="page" :page-count="pages" />
      </nav>
      <span class="text-muted small" :class="{ 'ms-auto': pages <= 1 }">
        {{ docs.length }} {{ docs.length === 1 ? 'item' : 'items' }}
      </span>
    </div>

    <table class="table table-sm table-bordered table-striped">
      <thead>
        <tr>
          <th>Uploaded</th><th>File</th><th>Customer Shop</th><th>RO #</th><th>VIN</th><th>Vehicle</th>
          <th>Inspection</th><th>Opportunity</th><th>Outcome</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in pagedDocs" :key="row.repairDocument">
          <td>{{ formatDate(row.createdOn) }}</td>
          <td>
            <a href="#" title="Download this PDF" @click.prevent="download(row)">
              <i class="fas fa-file-download me-1"></i>{{ row.fileName }}
            </a>
          </td>
          <td>
            <template v-if="row.shopName">{{ row.shopName }}</template>
            <!-- An upload with no shop recorded is ordinary (it predates the shop field, or was made
                 without one), so it is said plainly rather than left as a bare dash. -->
            <span v-else class="text-muted small">Not specified</span>
            <div v-if="row.shopEmail || row.shopPhone" class="text-muted small">
              {{ row.shopEmail || row.shopPhone }}
            </div>
          </td>
          <td>{{ row.roNumber }}</td>
          <td>{{ row.vin }}</td>
          <td>{{ vehicle(row) }}</td>
          <td>{{ row.adasInspection || '-' }}</td>
          <!-- An inspection with no opportunity is a normal outcome (nothing to calibrate, or it went
               to another provider), so this is blank rather than an error. -->
          <td>
            <router-link v-if="row.opportunity" :to="opportunityLink(row)">
              {{ row.opportunity }}
            </router-link>
          </td>
          <!-- The reason an upload produced no inspection, or produced one with nothing to calibrate,
               belongs here: otherwise the only way to find out is to go and look in ADAS Map. -->
          <td>
            <span v-if="outcome(row).label" class="badge" :class="outcome(row).css">{{ outcome(row).label }}</span>
            <div v-if="outcome(row).detail" class="text-muted small">{{ outcome(row).detail }}</div>
          </td>
        </tr>
        <tr v-if="!docs.length && !loading"><td colspan="9" class="text-center text-muted">No uploaded documents.</td></tr>
      </tbody>
    </table>

  </div>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import axios from 'axios'
import Multiselect from '@vueform/multiselect'
import { authStore } from '@/stores/auth'
import { estimateStore } from '@/stores/estimate'
import { locationStore } from '@/stores/location'
import ZPaginate from '@/components/utilities/elements/Paginate.vue'
export default {
  name: 'UploadEstimateReviewView',
  components: { Multiselect, ZPaginate },
  data() {
    return { docs: [], loading: false, errorMessage: '', notConfigured: false, selectedFirm: null, page: 1, pageSize: 10 }
  },
  computed: {
    ...mapState(authStore, ['token', 'profile']),
    isSuperAdmin() {
      return !!(this.profile && this.profile.roles && this.profile.roles[0] === 1)
    },
    // The API returns the firm's whole list in one call, so the paging is done here. Ten a page to
    // match the rest of the app.
    pages() {
      return Math.max(1, Math.ceil(this.docs.length / this.pageSize))
    },
    pagedDocs() {
      const start = (this.page - 1) * this.pageSize
      return this.docs.slice(start, start + this.pageSize)
    }
  },
  async mounted() {
    await this.load()
  },
  methods: {
    ...mapActions(estimateStore, ['getDocuments']),
    ...mapActions(locationStore, ['searchLocations']),
    // The opportunity list opens a detail this same way: there is no /opportunity/:id route, the
    // vehicle-details page is where an opportunity is read, and it wants the VIN with it.
    opportunityLink(row) {
      return `/vehicle-details/${row.opportunityVin || row.vin}/${row.adasInspection}/location/${row.opportunity}`
    },
    async fetchLocations(query) {
      // searchLocations builds its query string from these exact keys; anything else arrives as
      // "search=undefined" and matches nothing.
      return await this.searchLocations({
        search: query,
        type: null,
        firmType: null,
        firm: this.profile.firm
      })
    },
    formatDate(d) { return d ? new Date(d).toLocaleString() : '' },
    // One column: a VIN identifies the job to a system, not to the person reading the list.
    vehicle(row) {
      return [row.vehicleYear, row.vehicleMake, row.vehicleModel].filter((p) => p).join(' ')
    },
    // Three things a customer needs to tell apart: it never became an inspection, it became one that
    // needed no calibration (so no opportunity was raised), or it worked.
    outcome(row) {
      if (row.status === 'Rejected' || !row.adasInspection) {
        return {
          label: 'Not inspected',
          css: 'bg-danger',
          detail: row.statusReason || 'No inspection was created from this estimate.'
        }
      }
      if (row.numFound === 0) {
        return {
          label: 'No calibrations',
          css: 'custom-badge-attention',
          detail: 'The inspection found nothing requiring calibration, so no opportunity was created.'
        }
      }
      // No badge on the ordinary outcome: a column where almost every row says the same thing is noise.
      // The count underneath already says it worked.
      return {
        label: null,
        css: '',
        detail: row.numFound ? `${row.numFound} calibration${row.numFound === 1 ? '' : 's'} found` : ''
      }
    },
    async load() {
      this.loading = true; this.errorMessage = ''; this.notConfigured = false
      try {
        this.docs = await this.getDocuments(this.isSuperAdmin ? this.selectedFirm : null)
        this.page = 1
      } catch (e) {
        // Surface what the API actually said. It distinguishes "this firm is not set up for ADAS Map"
        // from a genuine failure, and hiding that behind a generic message is what made this page look
        // broken rather than unconfigured.
        // Error envelope is { error: { code, message } }; tolerate a flat shape too.
        const d = e && e.response && e.response.data
        const err = (d && typeof d.error === 'object' && d.error) || d || null
        this.notConfigured = !!err && (err.code || err.Code) === 'firm_not_configured'
        this.errorMessage =
          (err && (err.message || err.Message || (typeof err.error === 'string' && err.error))) ||
          (this.isSuperAdmin && !this.selectedFirm
            ? 'Choose a firm to see its uploaded estimates.'
            : 'Failed to load uploaded documents.')
        this.docs = []
        this.page = 1
      } finally {
        this.loading = false
      }
    },
    async download(row) {
      if (!row || !row.repairDocument) return
      try {
        const apiUrl = window.config ? window.config.apiUrl : ''
        const resp = await axios.get(`${apiUrl}/estimate/document/${row.repairDocument}/download`, {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${this.token}` }
        })
        const url = URL.createObjectURL(resp.data)
        const a = document.createElement('a')
        a.href = url
        a.download = row.fileName || `estimate-${row.repairDocument}.pdf`
        document.body.appendChild(a); a.click(); document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('download failed', e)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #6c757d;
}
.custom-firm-picker {
  width: 320px;
}
// The pager carries Bootstrap's default bottom margin, which pushes it out of line with the buttons.
.custom-pager :deep(.pagination) {
  margin-bottom: 0;
}
// Darker than bg-warning: this is the one outcome a customer needs to notice at a glance.
.custom-badge-attention {
  background-color: #b8860b;
  color: #fff;
  font-size: 0.8rem;
}
</style>
